import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { Plus } from 'lucide-react'
import { projetoService } from '../services/projeto'
import { ProjetoFilters } from '../tipos/projeto'
import { FormularioProjeto } from '../componentes/FormularioProjeto'
import { ProjetoList } from '../componentes/ListaProjeto'
import { ProjetoFilters as FiltersComponent } from '../componentes/FiltrosProjeto'

function Projetos() {
  const [showForm, setShowForm] = useState(false)
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null)
  const [filters, setFilters] = useState<ProjetoFilters>({})

  const {
    data: projetos,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ['projetos', filters],
    () => projetoService.buscarProjetos(filters),
    {
      keepPreviousData: true,
    }
  )

  // Query para buscar projeto completo quando editando
  const {
    data: projetoCompleto,
    isLoading: isLoadingProjetoCompleto,
  } = useQuery(
    ['projeto', editingProjectId],
    () => projetoService.getProjetoById(editingProjectId!),
    {
      enabled: !!editingProjectId,
      staleTime: 0, // Sempre considerar stale para recarregar
      refetchOnMount: 'always', // Sempre recarregar ao montar
    }
  )

  // Effect para controlar quando abrir o modal de edição
  // Abre o modal imediatamente quando editingProjectId é definido para mostrar loading
  useEffect(() => {
    if (editingProjectId && !showForm) {
      // Abre o modal imediatamente - o FormularioProjeto mostrará loading enquanto os dados carregam
      setShowForm(true)
    }
  }, [editingProjectId, showForm])



  const handleSearch = (newFilters: ProjetoFilters) => {
    setFilters(newFilters)
  }

  const handleEditProject = (projeto: any) => {
    // O serviço já normaliza o ID para 'id' (minúsculo)
    const projectId = projeto.id
    if (projectId) {
      // Define o ID primeiro - o useEffect vai abrir o modal quando os dados estiverem prontos
      setEditingProjectId(String(projectId))
      // Não abre o modal imediatamente - espera os dados carregarem
    }
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingProjectId(null)
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Erro ao carregar projetos</h3>
          <p className="text-gray-600">Tente novamente mais tarde.</p>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projetos</h1>
          <p className="mt-1 text-sm text-gray-600">Gerencie todos os projetos do sistema</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 shadow-theme-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-2 inline" />
            Novo Projeto
          </button>
        </div>
      </div>

      {/* Filters */}
      <FiltersComponent onSearch={handleSearch} />

      {/* Projects List */}
      <ProjetoList
        projetos={projetos?.data || []}
        isLoading={isLoading}
        onRefresh={refetch}
        onEditProject={handleEditProject}
      />

      {/* Project Form Modal */}
      <FormularioProjeto
        isOpen={showForm}
        onClose={handleCloseForm}
        projeto={editingProjectId ? projetoCompleto : undefined}
        isEditing={!!editingProjectId}
        isLoadingProjeto={!!editingProjectId && isLoadingProjetoCompleto}
      />

    </>
  )
}

export default Projetos
